// Centralized derivation for the standard set of projection milestones.
// Strategies call this from `computeMonth` so the rules stay uniform across
// products even though the call site is per-strategy. Product-specific
// milestones (e.g. shared-pool exhausted) can be appended by the strategy.
//
// All rules are evaluated against the current month's open state, close
// state, deltas, context, and the immediately prior month. No look-ahead.

import { carePhaseDefs } from '../../../CarePhaseDef';
import { careEnvironmentDefs } from '../../../CareEnvironmentDef';
import { getLtcPool } from './projectionTotals';
import { DAYS_IN_MONTH } from './sharedConstants';

export interface DeriveStandardMilestonesInput<TState = unknown, TDeltas = unknown> {
  context: MonthContext;
  facts: MonthlyCareFacts;
  openState: MonthlyState<TState>;
  closeState: MonthlyState<TState>;
  deltas: MonthlyDeltas<TDeltas>;
  priorMonth: MonthlyEntry<TState, TDeltas> | null;
  // Total dollars paid by the policy this month (regular payout + any
  // retroactive lump in strategyDeltas). Used by the ep-lump milestone.
  // Strategies that have a retroactive lump pass it here; products without
  // one can pass 0.
  retroactiveLumpPaid?: number;
  // Floor used by the min-DB-reached milestone. Default 0 (milestone
  // effectively disabled). Strategies should pass the policy's
  // `policyMinimumGuaranteedBenefitAmount`.
  minimumGuaranteedDeathBenefit?: number;
  // When true, the client is already on LTC at the start of the projection,
  // so the `ltc-begins` milestone is suppressed on month 0 (it would otherwise
  // misleadingly label an in-progress claim as "LTC begins").
  clientHasStartedLtc?: boolean;
  // When true, the policy has at least one non-zero inflation rate
  // (rider, base pool, or EOB pool), so policy anniversaries actually
  // change something on the policy and the `anniversary` milestone is
  // worth surfacing. When false (flat policy), the anniversary milestone
  // is suppressed. Default false.
  hasInflationProtection?: boolean;
}

function carePhaseLabel(value: CarePhase): string {
  return carePhaseDefs[value]?.label ?? String(value);
}

function careEnvironmentLabel(value: CareEnvironment): string {
  return careEnvironmentDefs[value]?.label ?? String(value);
}

export function deriveStandardMilestones<TState, TDeltas>(
  input: DeriveStandardMilestonesInput<TState, TDeltas>,
): ProjectionMilestone[] {
  const {
    context,
    facts,
    openState,
    closeState,
    priorMonth,
    retroactiveLumpPaid = 0,
    minimumGuaranteedDeathBenefit = 0,
    clientHasStartedLtc = false,
    hasInflationProtection = false,
  } = input;
  const ms: ProjectionMilestone[] = [];

  // LTC begins: the first month of the projection marks the start of
  // projected LTC need (1-ADL threshold). Suppressed when the client is
  // already on LTC at projection start — the projection isn't telling us LTC
  // "begins" in that case, it's mid-claim.
  if (!priorMonth && !clientHasStartedLtc) {
    const label =
      context.clientAge != null
        ? `LTC begins at age ${context.clientAge}`
        : 'LTC begins';
    ms.push({ kind: 'ltc-begins', label });
  }

  // Claim begins: 2-ADL gate clears. The gate "blocks" a month only when the
  // <2 ADL bucket consumes the ENTIRE month (only happens in earlyCare with a
  // clientAdlCount of 1). Fires on the first month with any post-gate days —
  // either month 0 (if the gate was never active) or the month in which the
  // bucket finishes draining. The bucket usually ends mid-month, and that
  // partial month can already pay benefits, so the milestone must land on it
  // (not the following fully-clear month) to match the table's
  // "benefits activated" boundary.
  const gateActive = facts.non2AdlDays >= DAYS_IN_MONTH;
  const priorGateActive = priorMonth
    ? priorMonth.facts.non2AdlDays >= DAYS_IN_MONTH
    : false;
  if (!gateActive && (priorGateActive || !priorMonth)) {
    ms.push({ kind: 'claim-start', label: 'Claim begins' });
  }

  // EP retroactive lump sum paid.
  if (retroactiveLumpPaid > 0) {
    ms.push({ kind: 'ep-lump', label: 'WP Satisfied' });
  }

  // LTC pool fully drawn down this month.
  const openLtc = getLtcPool(openState);
  const closeLtc = getLtcPool(closeState);
  if (openLtc > 0 && closeLtc === 0) {
    ms.push({ kind: 'pool-exhausted', label: 'LTC pool exhausted' });
  }

  // Death benefit hit the guaranteed floor this month.
  if (
    openState.pools.deathBenefit > minimumGuaranteedDeathBenefit &&
    closeState.pools.deathBenefit <= minimumGuaranteedDeathBenefit
  ) {
    ms.push({ kind: 'min-db-reached', label: 'Min death benefit' });
  }

  // Policy anniversary: the policy year ticked over since the prior month.
  // Only surfaced when the policy has some form of inflation protection —
  // on a flat policy the anniversary is a no-op tick worth nothing to advisors.
  if (
    hasInflationProtection &&
    priorMonth &&
    priorMonth.context.policyYear < context.policyYear
  ) {
    ms.push({ kind: 'anniversary', label: 'Anniversary' });
  }

  // Phase / care-setting transitions vs. the prior month. When both change in
  // the same month (typical at the first care month), surface them as one
  // combined "<phase> · <environment>" badge rather than two adjacent chips —
  // the pair reads as a single care transition.
  const phaseChanged =
    priorMonth != null && priorMonth.context.carePhase !== context.carePhase;
  const envChanged =
    priorMonth != null && priorMonth.context.careEnvironment !== context.careEnvironment;
  if (phaseChanged && envChanged) {
    ms.push({
      kind: 'phase-change',
      label: `${carePhaseLabel(context.carePhase)} · ${careEnvironmentLabel(context.careEnvironment)}`,
    });
  } else if (phaseChanged) {
    ms.push({ kind: 'phase-change', label: carePhaseLabel(context.carePhase) });
  } else if (envChanged) {
    ms.push({ kind: 'env-change', label: careEnvironmentLabel(context.careEnvironment) });
  }

  return ms;
}
